/**
 * Terms of Service modal component.
 *
 * Blocking modal that requires users to accept the Terms of Service
 * before using the platform. Cannot be dismissed without acceptance.
 * Requires scrolling to the bottom before the Accept button activates.
 */

import React, { useState, useRef, useCallback } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { handleError } from '../../utils/errorHandler';
import { TermsContent } from '../legal';

interface TermsOfServiceModalProps {
  /** Whether modal is visible */
  isOpen: boolean;
}

/**
 * Blocking Terms of Service modal.
 * No close button, no backdrop dismiss. User must scroll and accept.
 */
export const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const { acceptTermsOfService } = useAuth();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    // Consider "scrolled to bottom" when within 20px of the end
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    if (atBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  const handleAccept = async (): Promise<void> => {
    try {
      setIsAccepting(true);
      setError(null);
      await acceptTermsOfService();
    } catch (err) {
      setError(handleError(err, 'TermsOfService', t('tos.acceptError')));
    } finally {
      setIsAccepting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tos-modal-title"
    >
      <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl w-full max-w-[calc(100vw-1rem)] sm:max-w-2xl flex flex-col max-h-[calc(100vh-1rem)] sm:max-h-[85vh]">
        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/10 shrink-0">
          <h2 id="tos-modal-title" className="text-xl sm:text-2xl font-bold text-white">
            {t('tos.title')}
          </h2>
          <p className="text-sm text-white/60 mt-1">{t('tos.subtitle')}</p>
        </div>

        {/* Scrollable Content */}
        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 scroll-smooth-touch"
        >
          <TermsContent />
        </div>

        {/* Footer with Accept Button */}
        <div className="px-4 sm:px-6 py-4 border-t border-white/10 shrink-0 space-y-3">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Scroll hint */}
          {!hasScrolledToBottom && (
            <p className="text-xs text-white/40 text-center">{t('tos.scrollToAccept')}</p>
          )}

          <button
            type="button"
            onClick={handleAccept}
            disabled={!hasScrolledToBottom || isAccepting}
            className="w-full min-h-[44px] py-3 px-4 rounded-lg font-medium text-base transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-purple-600 hover:bg-purple-500 text-white disabled:hover:bg-purple-600"
          >
            {isAccepting ? t('tos.accepting') : t('tos.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};
